import React, { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import MascotPlaceholder from "@/components/MascotPlaceholder";
import AuthPromptModal from "@/components/AuthPromptModal";
import TaskCard from "@/components/TaskCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CaretDown, Plus, X, ArrowRight } from "@phosphor-icons/react";

// ------------------------------------------------------------------
// Phase 3 – "A bordo" (partial). Currently a single collapsible card:
// "Gestione spese in USA" (one-off expenses table). Placeholder-ready
// for more sections to be added as the phase gets built out.
// ------------------------------------------------------------------

function useAutoSave(state, ready, endpoint, onSaved) {
  const [status, setStatus] = useState("idle");
  const timerRef = useRef(null);
  useEffect(() => {
    if (!ready) return;
    setStatus("saving");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        await api.put(endpoint, state);
        setStatus("saved");
        if (typeof onSaved === "function") onSaved();
        setTimeout(() => setStatus(s => s === "saved" ? "idle" : s), 1500);
      } catch { setStatus("idle"); }
    }, 500);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [state, ready, endpoint, onSaved]);
  return status;
}

function SaveIndicator({ status, t }) {
  if (status === "saving") return <span data-testid="save-indicator" className="text-xs text-ink-soft/70 font-sans">{t.checkin.savingIndicator}</span>;
  if (status === "saved")  return <span data-testid="save-indicator" className="text-xs text-sage font-sans font-semibold">✓ {t.checkin.savedIndicator}</span>;
  return null;
}

function CollapsibleCard({ id, title, desc, isOpen, onToggle, children }) {
  return (
    <section
      data-testid={`abordo-card-${id}`}
      data-open={isOpen ? "true" : "false"}
      className={`rounded-3xl border bg-surface-card overflow-hidden transition-colors ${
        isOpen ? "border-terracotta/40" : "border-border hover:border-terracotta/30"
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`abordo-card-body-${id}`}
        data-testid={`abordo-card-toggle-${id}`}
        className="w-full text-left p-5 sm:p-6 flex items-start gap-4"
      >
        <div className="h-6 w-1 rounded-full bg-terracotta shrink-0 mt-1" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-extrabold text-xl sm:text-2xl text-ink tracking-tight leading-tight">
            {title}
          </h2>
          {desc && <p className="text-sm text-ink-soft font-sans mt-1 leading-relaxed">{desc}</p>}
        </div>
        <CaretDown
          size={20}
          weight="bold"
          className={`text-terracotta shrink-0 mt-1 transition-transform duration-300 ${isOpen ? "rotate-180" : "rotate-0"}`}
          aria-hidden="true"
        />
      </button>
      <div
        id={`abordo-card-body-${id}`}
        data-testid={`abordo-card-body-${id}`}
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          <div className="px-5 sm:px-6 pb-6 pt-1 space-y-6">{children}</div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Scuola USA operativa — uses the shared TaskCard component so its visual
// pattern (font, checkbox, deadline hint, Approfondisci panel with tri-color
// Perché/Cosa fare/Attenzione headers) stays identical to the checklist
// items in "Organizza la partenza".
// ─────────────────────────────────────────────────────────────────────────
function SchoolTasksSection({ t, content, doneIds, onToggleDone }) {
  const [openId, setOpenId] = useState(null);
  const done = new Set(doneIds || []);
  const labels = {
    learnMore: t.modes?.organizzativa?.checklistLearnMore || "Approfondisci",
    showLess:  t.modes?.organizzativa?.checklistShowLess  || "Nascondi",
    why:       t.modes?.organizzativa?.checklistDetailsWhy || "Perché",
    how:       t.modes?.organizzativa?.checklistDetailsHow || "Cosa fare",
    careful:   t.modes?.organizzativa?.checklistDetailsWarning || "Attenzione",
  };
  return (
    <section data-testid="abordo-school-tasks-section" className="space-y-3">
      <div>
        <h2 className="font-display font-black text-xl sm:text-2xl text-ink tracking-tight">
          {content.title}
        </h2>
        {content.desc && (
          <p className="text-sm text-ink-soft mt-1 leading-relaxed font-sans">{content.desc}</p>
        )}
      </div>
      <div className="space-y-2">
        {content.items.map(task => (
          <TaskCard
            key={task.id}
            testId={`abordo-school-task-${task.id}`}
            title={task.text}
            deadline_hint={task.when}
            why={task.why}
            how={task.how}
            careful={task.careful}
            done={done.has(task.id)}
            expanded={openId === task.id}
            onToggle={() => onToggleDone(task.id)}
            onToggleExpand={() => setOpenId(prev => prev === task.id ? null : task.id)}
            labels={labels}
          />
        ))}
      </div>
    </section>
  );
}

function ExpensesTable({ t, content, rows, onChange }) {
  const byId = Object.fromEntries(rows.map(r => [r.id, r]));
  const seedRows = content.items.map(si => ({
    id: si.id, label: si.label, amount: byId[si.id]?.amount ?? null, custom: false,
  }));
  const customRows = rows.filter(r => r.custom);
  const total = [...seedRows, ...customRows]
    .map(r => Number(r.amount))
    .filter(v => !Number.isNaN(v))
    .reduce((a, b) => a + b, 0);

  const updateAmount = (id, val) => {
    const clean = val === "" ? null : Number(val);
    onChange([
      ...rows.filter(r => r.id !== id),
      { id, amount: Number.isNaN(clean) ? null : clean, custom: byId[id]?.custom || false, label: byId[id]?.label },
    ]);
  };
  const updateLabel = (id, label) => onChange(rows.map(r => r.id === id ? { ...r, label } : r));
  const addCustom = () => {
    const newId = `custom_abordo_${Date.now()}`;
    onChange([...rows, { id: newId, label: "", amount: null, custom: true }]);
  };
  const removeCustom = (id) => onChange(rows.filter(r => r.id !== id));

  return (
    <div data-testid="abordo-expenses-table" className="rounded-3xl border border-border bg-surface-card p-5 sm:p-6">
      <h3 className="font-display font-extrabold text-xl text-ink tracking-tight">{content.table_title}</h3>
      <p className="text-xs text-ink-soft font-sans uppercase tracking-widest mt-1">{content.table_subtitle}</p>

      <ul className="mt-4 space-y-1.5">
        {seedRows.map(r => (
          <li key={r.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-sand/10">
            <span className="flex-1 text-sm text-ink font-sans">{r.label}</span>
            <Input
              data-testid={`abordo-amount-${r.id}`}
              type="number" inputMode="decimal" min={0} step="0.01"
              placeholder={t.checkin.amountPh}
              value={r.amount ?? ""}
              onChange={(e) => updateAmount(r.id, e.target.value)}
              className="w-32 h-9 text-sm text-right rounded-lg"
            />
          </li>
        ))}
        {customRows.map(r => (
          <li key={r.id} data-testid={`abordo-custom-row-${r.id}`} className="flex items-center gap-2 p-2 rounded-xl bg-sand/10">
            <Input
              data-testid={`abordo-custom-label-${r.id}`}
              type="text" placeholder={t.checkin.customLabelPh}
              value={r.label || ""} onChange={(e) => updateLabel(r.id, e.target.value)}
              className="flex-1 h-9 text-sm rounded-lg"
            />
            <Input
              data-testid={`abordo-amount-${r.id}`}
              type="number" inputMode="decimal" min={0} step="0.01"
              placeholder={t.checkin.amountPh}
              value={r.amount ?? ""} onChange={(e) => updateAmount(r.id, e.target.value)}
              className="w-32 h-9 text-sm text-right rounded-lg"
            />
            <button
              type="button" data-testid={`abordo-remove-${r.id}`}
              onClick={() => removeCustom(r.id)} aria-label={t.checkin.removeRow}
              className="w-8 h-8 rounded-lg text-ink-soft hover:text-terracotta hover:bg-terracotta/10 flex items-center justify-center shrink-0"
            >
              <X size={14} weight="bold" />
            </button>
          </li>
        ))}
      </ul>

      <div className="mt-4 flex items-center justify-between gap-3 pt-4 border-t border-border/60">
        <Button
          type="button" variant="ghost" size="sm" onClick={addCustom}
          data-testid="abordo-add"
          className="text-terracotta hover:bg-terracotta/10 rounded-full"
        >
          <Plus size={14} weight="bold" className="mr-1" />
          {t.checkin.addRow}
        </Button>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-widest text-ink-soft font-sans font-bold">{t.checkin.total}</div>
          <div data-testid="abordo-total" className="font-display font-extrabold text-2xl text-ink">
            € {total.toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ABordo() {
  const { t, lang, user, loadingUser, refreshPhaseState } = useApp();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [state, setState] = useState(null);
  const [ready, setReady] = useState(false);
  const [openId, setOpenId] = useState(null);
  // Anonymous users get an in-memory empty state and any mutation attempt
  // opens the AuthPromptModal — same pattern used on /organizza and /checkin.
  const isAnon = !user && !loadingUser;
  const [authPromptOpen, setAuthPromptOpen] = useState(false);

  // Fire the Plausible custom event once per mount, aligned with the pattern
  // used on Landing.jsx and Explorativa.jsx. Enables funnel analysis:
  // "how many visitors reach Phase 3 content before creating an account?"
  useEffect(() => { trackEvent("ABordo Viewed"); }, []);

  useEffect(() => {
    if (loadingUser) return;
    (async () => {
      try {
        const contentReq = api.get(`/abordo/content?lang=${lang}`);
        // Fetch state only for logged users; anon starts fresh in-memory.
        const stateReq = user ? api.get("/abordo/state") : Promise.resolve({ data: { expenses: [], school_tasks_done: [] } });
        const [c, s] = await Promise.all([contentReq, stateReq]);
        setContent(c.data);
        setState(s.data);
        setReady(true);
      } catch { /* fail-soft */ }
    })();
  }, [user, loadingUser, lang]);

  // Autosave only writes to the server for logged-in users. For anon we
  // keep state entirely ephemeral (the AuthPromptModal gates any mutation
  // before it reaches this function).
  const saveStatus = useAutoSave(state, ready && !!user, "/abordo/state", refreshPhaseState);

  if (loadingUser) return <div className="p-8 text-ink-soft">{t.common.loading}</div>;
  if (!ready || !content || !state) return <div className="p-8 text-ink-soft">{t.common.loading}</div>;

  const toggle = (id) => setOpenId(prev => prev === id ? null : id);

  // Wrap any state-mutating callback so anonymous users see the auth prompt
  // instead of silently changing UI they can't persist.
  const guarded = (mutator) => (...args) => {
    if (isAnon) { setAuthPromptOpen(true); return; }
    mutator(...args);
  };

  return (
    <div className="max-w-4xl mx-auto px-5 py-8 space-y-10">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-terracotta font-semibold font-sans">
            {t.phase.aboard}
          </p>
          <h1 data-testid="abordo-title" className="font-display font-black text-3xl sm:text-4xl text-ink mt-1 tracking-tight">
            {t.abordo.title}
          </h1>
          <p className="text-ink-soft mt-2 leading-relaxed max-w-xl">
            {t.abordo.subtitle}
          </p>
        </div>
        <MascotPlaceholder emotion="happy" src="/assets/boardy-energico.png" />
      </div>

      <div className="flex justify-end -mt-6 h-4"><SaveIndicator status={saveStatus} t={t} /></div>

      {/* Scuola USA operativa — list of tasks, each with its own expandable panel */}
      {content.school_tasks && (
        <SchoolTasksSection
          t={t}
          content={content.school_tasks}
          doneIds={state.school_tasks_done || []}
          onToggleDone={guarded((taskId) => setState(s => {
            const current = new Set(s.school_tasks_done || []);
            if (current.has(taskId)) current.delete(taskId);
            else current.add(taskId);
            return { ...s, school_tasks_done: Array.from(current) };
          }))}
        />
      )}

      <div className="space-y-3">
        <CollapsibleCard
          id="expenses"
          title={content.expenses.title}
          desc={content.expenses.desc}
          isOpen={openId === "expenses"}
          onToggle={() => toggle("expenses")}
        >
          <div data-testid="abordo-expenses-intro" className="rounded-2xl bg-sand/25 border border-sand/50 p-4 text-sm text-ink font-sans leading-relaxed italic">
            {content.expenses.intro_note}
          </div>
          <ExpensesTable
            t={t}
            content={content.expenses}
            rows={state.expenses || []}
            onChange={guarded((rows) => setState(s => ({ ...s, expenses: rows })))}
          />
        </CollapsibleCard>

        {/* Vita quotidiana — single collapsible card with links to existing FAQ entries */}
        {content.daily_life && (
          <CollapsibleCard
            id="daily_life"
            title={content.daily_life.title}
            desc={content.daily_life.desc}
            isOpen={openId === "daily_life"}
            onToggle={() => toggle("daily_life")}
          >
            <div data-testid="abordo-daily-life-intro" className="rounded-2xl bg-sand/25 border border-sand/50 p-4 text-sm text-ink font-sans leading-relaxed italic">
              {content.daily_life.intro_note}
            </div>
            <ul data-testid="abordo-daily-life-links" className="space-y-2">
              {content.daily_life.links.map(link => (
                <li key={link.faq_id}>
                  <Link
                    to={`/faq?open=${link.faq_id}`}
                    data-testid={`abordo-daily-life-link-${link.faq_id}`}
                    className="flex items-center gap-3 p-3 rounded-xl border border-border bg-surface hover:border-terracotta/50 hover:bg-terracotta/5 transition-colors group"
                  >
                    <span className="flex-1 text-sm sm:text-base text-ink font-sans font-semibold leading-snug">
                      {link.label}
                    </span>
                    <ArrowRight
                      size={16}
                      weight="bold"
                      className="text-terracotta shrink-0 transition-transform group-hover:translate-x-0.5"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </CollapsibleCard>
        )}
      </div>

      <AuthPromptModal
        open={authPromptOpen}
        onOpenChange={setAuthPromptOpen}
        reason="save-abordo"
        afterPath="/abordo"
      />
    </div>
  );
}
